"use client"

import { useEffect, useRef, useState } from "react"

/**
 * A shop's Instagram, as six photos in a grid that sits in our layout.
 *
 * Instagram's own profile embed (instagram.com/<handle>/embed/) drawn in an
 * iframe and cropped to the grid alone: its header — avatar, follower count,
 * their logo — is shifted up out of a clipping frame. Instagram does not let
 * a site pull a profile's posts any other way without that account's
 * permission, so this is the one version that works for every shop with an
 * Instagram link today.
 *
 * The crop is measured, not guessed: the header is 147px tall up to 480px of
 * width and 158px above that, and the grid under it is two rows of square
 * cells, a third of the width each. Re-measured on resize, because the
 * frame's width is the only input.
 */
const HEADER_NARROW = 147
const HEADER_WIDE = 158
const NARROW_MAX = 480

/** "https://instagram.com/foo" → "foo". Null for anything that is not a
    plain profile link (a post, a reel), which has no profile grid. */
export function instagramHandle(url: string | null | undefined): string | null {
  if (!url) return null
  const m = url.match(/instagram\.com\/([A-Za-z0-9._]{1,30})\/?$/i)
  if (!m) return null
  const handle = m[1]
  return ["p", "reel", "reels", "explore", "stories", "tv"].includes(handle.toLowerCase()) ? null : handle
}

export function InstagramGrid({ handle, title }: { handle: string; title: string }) {
  const frame = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)

  useEffect(() => {
    const el = frame.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => setWidth(Math.round(entry.contentRect.width)))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const header = width > NARROW_MAX ? HEADER_WIDE : HEADER_NARROW
  // Two rows of square cells; the 1px is the hairline under the last row.
  const grid = Math.round((width * 2) / 3)

  // 55% of the column from md up: at full width each photo was bigger than
  // the listing's own gallery tiles, and the grid outweighed the shop. Full
  // width on phones, where 55% would make the photos thumbnails.
  return (
    <div className="space-y-3 w-full md:w-[55%]">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-sm font-medium text-foreground">{title}</h2>
        <a
          href={`https://www.instagram.com/${handle}/`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          @{handle}
        </a>
      </div>
      <div
        ref={frame}
        className="relative w-full overflow-hidden rounded-[1.75rem] bg-muted"
        style={{ height: width ? grid - 1 : undefined, aspectRatio: width ? undefined : "3 / 2" }}
      >
        {width > 0 && (
          <iframe
            src={`https://www.instagram.com/${handle}/embed/`}
            title={`${title}: @${handle}`}
            loading="lazy"
            scrolling="no"
            className="absolute left-0 w-full border-0"
            style={{ top: -header, height: header + grid + 120 }}
          />
        )}
      </div>
    </div>
  )
}
